// src/config/standards/computerScience.js

// CSTA K–12 Computer Science Standards (2017)
// Domains (Concepts): Computing Systems (CS), Networks & the Internet (NI),
// Data & Analysis (DA), Algorithms & Programming (AP), Impacts of Computing (IC)
// Grade bands mapped as:
//   Level 1A = K–2  → Kindergarten, 1st Grade, 2nd Grade
//   Level 1B = 3–5  → 3rd Grade, 4th Grade, 5th Grade
//   Level 2  = 6–8  → 6th Grade, 7th Grade, 8th Grade
//   Level 3A = 9–10 → High School (cluster: "HS—9–10 (3A)")
//   Level 3B = 11–12→ High School (cluster: "HS—11–12 (3B)")

// -------------------------------
// Helpers to build standard items
// -------------------------------
const csItem = (grade, code, description) => ({
  id: `Computer Science-${grade}-${code}`,
  code,
  description,
});

// -------------------------------
// LEVEL 1A (K–2)
// -------------------------------
const L1A = {
  "Computing Systems": [
    csItem("K–2", "1A-CS-01", "Select and use common features of computing devices; identify input, output, processing, and storage at a basic level."),
    csItem("K–2", "1A-CS-02", "Use accurate words to name and describe internal and external parts of a computing system (e.g., keyboard, mouse, screen, battery)."),
    csItem("K–2", "1A-CS-03", "Use a computing device to store, retrieve, and manage information for simple tasks."),
  ],
  "Networks and the Internet": [
    csItem("K–2", "1A-NI-04", "Explain that computers can connect to other devices and the internet to share information."),
  ],
  "Data & Analysis": [
    csItem("K–2", "1A-DA-05", "Store, copy, search, retrieve, modify, and delete information using a computing device; recognize information as data."),
    csItem("K–2", "1A-DA-06", "Collect and present data using digital tools (e.g., pictures, tallies, simple charts)."),
    csItem("K–2", "1A-DA-07", "Identify and describe simple patterns in data."),
  ],
  "Algorithms & Programming": [
    csItem("K–2", "1A-AP-08", "Model and follow step-by-step algorithms to complete tasks (e.g., daily routines, simple puzzles)."),
    csItem("K–2", "1A-AP-09", "Model how programs can represent and manipulate information using numbers or symbols."),
    csItem("K–2", "1A-AP-10", "Create programs using sequences and simple loops to solve very simple problems."),
    csItem("K–2", "1A-AP-11", "Break a task into ordered steps (decompose) before programming."),
    csItem("K–2", "1A-AP-12", "Identify and fix basic errors (debug) in algorithms or simple programs."),
    csItem("K–2", "1A-AP-13", "Use age-appropriate programming tools to create media or stories."),
    csItem("K–2", "1A-AP-14", "Use different strategies (e.g., checking steps) to debug simple programs."),
    csItem("K–2", "1A-AP-15", "Test and refine programs to improve how they work."),
  ],
  "Impacts of Computing": [
    csItem("K–2", "1A-IC-16", "Compare ways people use computing devices at home, school, and in the community."),
    csItem("K–2", "1A-IC-17", "Work respectfully and responsibly with others when using technology."),
    csItem("K–2", "1A-IC-18", "Keep personal information private and follow basic rules for safe, responsible technology use."),
  ],
};

// -------------------------------
// LEVEL 1B (3–5)
// -------------------------------
const L1B = {
  "Computing Systems": [
    csItem("3–5", "1B-CS-01", "Describe how internal and external parts of computing devices function together."),
    csItem("3–5", "1B-CS-02", "Model how hardware and software components work together as a system."),
    csItem("3–5", "1B-CS-03", "Describe how networks and the internet move information."),
  ],
  "Networks and the Internet": [
    csItem("3–5", "1B-NI-04", "Model how information is broken into packets and travels across networks and the internet."),
    csItem("3–5", "1B-NI-05", "Discuss potential problems (e.g., security, outages) and identify ways to protect devices and information."),
  ],
  "Data & Analysis": [
    csItem("3–5", "1B-DA-06", "Organize and present data using tables, graphs, and charts."),
    csItem("3–5", "1B-DA-07", "Use data to highlight or propose solutions to problems."),
    csItem("3–5", "1B-DA-08", "Compare how different data displays can emphasize different aspects of the same data."),
  ],
  "Algorithms & Programming": [
    csItem("3–5", "1B-AP-08", "Compare and refine multiple algorithms for the same task."),
    csItem("3–5", "1B-AP-09", "Create programs using variables, sequences, loops, and events."),
    csItem("3–5", "1B-AP-10", "Develop programs with control structures (e.g., conditionals) and event handling."),
    csItem("3–5", "1B-AP-11", "Decompose problems and build procedures to organize solutions."),
    csItem("3–5", "1B-AP-12", "Modify and reuse existing code or libraries in new projects."),
    csItem("3–5", "1B-AP-13", "Debug programs by identifying and fixing errors in algorithms and code."),
    csItem("3–5", "1B-AP-14", "Test and refine programs; use input/output to improve usability."),
    csItem("3–5", "1B-AP-15", "Describe an iterative development process and improve programs in steps."),
    csItem("3–5", "1B-AP-16", "Design programs that account for user needs and constraints."),
  ],
  "Impacts of Computing": [
    csItem("3–5", "1B-IC-18", "Discuss how computing has positive and negative impacts on people and society and the importance of safe/ethical use."),
  ],
};

// -------------------------------
// LEVEL 2 (6–8)
// -------------------------------
const L2 = {
  "Computing Systems": [
    csItem("6–8", "2-CS-01", "Describe internal/external components and how they interact in a computing system."),
    csItem("6–8", "2-CS-02", "Model how hardware and software components work together (e.g., memory, CPU, OS, apps)."),
    csItem("6–8", "2-CS-03", "Explain how networks and the internet send, route, and deliver data."),
    csItem("6–8", "2-CS-04", "Design projects that combine hardware and software to collect, process, or display information."),
  ],
  "Networks and the Internet": [
    csItem("6–8", "2-NI-04", "Explain how protocols, addressing, and routing enable data exchange at scale."),
    csItem("6–8", "2-NI-05", "Explain how encryption supports secure communication."),
    csItem("6–8", "2-NI-06", "Describe cybersecurity concerns and safe practices to protect devices and information."),
    csItem("6–8", "2-NI-07", "Evaluate physical and digital security measures (e.g., passwords, firewalls, updates)."),
  ],
  "Data & Analysis": [
    csItem("6–8", "2-DA-08", "Collect data using a variety of tools and methods (including sensors) with attention to quality."),
    csItem("6–8", "2-DA-09", "Represent and transform data in multiple formats to emphasize key features."),
    csItem("6–8", "2-DA-10", "Use data to support claims, make predictions, or identify solutions."),
    csItem("6–8", "2-DA-11", "Refine computational models to better represent patterns, trends, or phenomena."),
  ],
  "Algorithms & Programming": [
    csItem("6–8", "2-AP-12", "Design and iteratively refine programs to solve problems."),
    csItem("6–8", "2-AP-13", "Decompose problems and create reusable procedures/functions (with parameters)."),
    csItem("6–8", "2-AP-14", "Use variables to store and update values; choose appropriate data types/structures for simple needs."),
    csItem("6–8", "2-AP-15", "Use control structures (conditionals, nested loops) to manage program flow."),
    csItem("6–8", "2-AP-16", "Incorporate user input, files, or data streams to affect program behavior."),
    csItem("6–8", "2-AP-17", "Systematically test and debug programs using a range of strategies."),
    csItem("6–8", "2-AP-18", "Collaborate on programs by dividing tasks and integrating components."),
    csItem("6–8", "2-AP-19", "Document programs and the development process for others to understand and use."),
  ],
  "Impacts of Computing": [
    csItem("6–8", "2-IC-20", "Weigh trade-offs of data collection and data sharing (benefits, risks, privacy)."),
    csItem("6–8", "2-IC-21", "Explain how computing innovations influence and are influenced by culture and society."),
    csItem("6–8", "2-IC-22", "Describe legal/ethical responsibilities (copyright, licensing, citation, fair use)."),
    csItem("6–8", "2-IC-23", "Identify issues of access, bias, and equity related to computing."),
  ],
};

// -------------------------------
// LEVEL 3A (HS 9–10)  &  LEVEL 3B (HS 11–12)
// Stored per-domain as HS clusters
// -------------------------------
const L3A = {
  "Computing Systems": [
    csItem("HS—9–10", "3A-CS-01", "Explain how abstraction manages complexity in computing systems."),
    csItem("HS—9–10", "3A-CS-02", "Explain how data (e.g., images, audio) is represented, stored, and processed."),
    csItem("HS—9–10", "3A-CS-03", "Evaluate how choices in hardware/OS/software enable or limit capabilities."),
  ],
  "Networks and the Internet": [
    csItem("HS—9–10", "3A-NI-04", "Explain how addressing, naming, and protocols enable the internet to scale."),
    csItem("HS—9–10", "3A-NI-05", "Explain trade-offs among network types, latency, bandwidth, and reliability."),
  ],
  "Data & Analysis": [
    csItem("HS—9–10", "3A-DA-06", "Create and evaluate data visualizations/transformations to reveal patterns."),
    csItem("HS—9–10", "3A-DA-07", "Translate among data representations; evaluate information loss and impact."),
    csItem("HS—9–10", "3A-DA-08", "Evaluate computational models’ assumptions, strengths, and limitations."),
  ],
  "Algorithms & Programming": [
    csItem("HS—9–10", "3A-AP-13", "Decompose complex problems; design reusable procedures and modules."),
    csItem("HS—9–10", "3A-AP-14", "Use libraries/APIs; write procedures with parameters and return values."),
    csItem("HS—9–10", "3A-AP-15", "Select data structures and control structures; justify design decisions."),
    csItem("HS—9–10", "3A-AP-16", "Design, implement, and present programs for specified users and contexts."),
    csItem("HS—9–10", "3A-AP-17", "Systematically test and debug based on requirements/specifications."),
    csItem("HS—9–10", "3A-AP-21", "Collaborate with tools (version control, issues) to manage program development."),
    csItem("HS—9–10", "3A-AP-22", "Create technical and user-facing documentation for programs."),
    csItem("HS—9–10", "3A-AP-23", "Evaluate and iteratively improve programs using feedback and testing evidence."),
  ],
  "Impacts of Computing": [
    csItem("HS—9–10", "3A-IC-24", "Evaluate effects of computing innovations on culture and society."),
    csItem("HS—9–10", "3A-IC-25", "Explain ownership/permissions; follow licensing and terms of use."),
    csItem("HS—9–10", "3A-IC-26", "Demonstrate ethical behavior and proper attribution/citation."),
    csItem("HS—9–10", "3A-IC-27", "Analyze issues of bias, access, and equity in computing."),
    csItem("HS—9–10", "3A-IC-28", "Describe laws/responsibilities related to digital behavior and data."),
    csItem("HS—9–10", "3A-IC-29", "Analyze privacy/security implications of data collection and storage."),
    csItem("HS—9–10", "3A-IC-30", "Explore computing careers and pathways."),
  ],
};

const L3B = {
  "Computing Systems": [
    csItem("HS—11–12", "3B-CS-01", "Compare levels of abstraction and interactions among components in complex systems."),
    csItem("HS—11–12", "3B-CS-02", "Evaluate how parallelism, distribution, and virtualization impact system performance and reliability."),
  ],
  "Networks and the Internet": [
    csItem("HS—11–12", "3B-NI-04", "Evaluate scalability and reliability of networks and internet architecture."),
    csItem("HS—11–12", "3B-NI-05", "Apply cybersecurity principles to design or evaluate strategies that secure systems and data."),
  ],
  "Data & Analysis": [
    csItem("HS—11–12", "3B-DA-06", "Analyze limitations of computing when making data-driven decisions."),
    csItem("HS—11–12", "3B-DA-07", "Design data collection/cleaning methods that reduce bias and improve validity."),
    csItem("HS—11–12", "3B-DA-08", "Create interactive visualizations; justify design choices for audience and task."),
  ],
  "Algorithms & Programming": [
    csItem("HS—11–12", "3B-AP-13", "Prototype algorithmic solutions for complex problems; articulate trade-offs."),
    csItem("HS—11–12", "3B-AP-14", "Create and use advanced procedural abstractions (e.g., higher-order functions)."),
    csItem("HS—11–12", "3B-AP-15", "Analyze and explain program behavior, including recursion and modular design."),
    csItem("HS—11–12", "3B-AP-16", "Compare algorithm efficiency (e.g., using Big-O) and justify choices."),
    csItem("HS—11–12", "3B-AP-17", "Use systematic testing/verification techniques and debugging strategies."),
    csItem("HS—11–12", "3B-AP-18", "Plan/manage large projects (e.g., agile practices); integrate components."),
    csItem("HS—11–12", "3B-AP-19", "Use collaborative workflows and version control for multi-file, multi-module programs."),
    csItem("HS—11–12", "3B-AP-20", "Produce comprehensive technical and user documentation for diverse audiences."),
  ],
  "Impacts of Computing": [
    csItem("HS—11–12", "3B-IC-24", "Analyze societal, economic, and political impacts of computing innovations."),
    csItem("HS—11–12", "3B-IC-25", "Evaluate legal/ethical considerations (privacy, copyright, licensing, data governance)."),
    csItem("HS—11–12", "3B-IC-26", "Demonstrate responsible digital behavior; manage digital footprint and identity."),
    csItem("HS—11–12", "3B-IC-27", "Investigate and propose ways to mitigate bias and inequity in computing."),
    csItem("HS—11–12", "3B-IC-28", "Explore career pathways and contribute to community through computing."),
  ],
};

// -------------------------------
// Export: full subject object
// -------------------------------
export const COMPUTER_SCIENCE_STANDARDS = {
  "Computer Science": {
    // K–2 share Level 1A
    "Kindergarten": L1A,
    "1st Grade": L1A,
    "2nd Grade": L1A,

    // 3–5 share Level 1B
    "3rd Grade": L1B,
    "4th Grade": L1B,
    "5th Grade": L1B,

    // 6–8 share Level 2
    "6th Grade": L2,
    "7th Grade": L2,
    "8th Grade": L2,

    // High School splits into 3A and 3B clusters per domain
    "High School": {
      "Computing Systems": {
        "HS—9–10 (3A)": L3A["Computing Systems"],
        "HS—11–12 (3B)": L3B["Computing Systems"],
      },
      "Networks and the Internet": {
        "HS—9–10 (3A)": L3A["Networks and the Internet"],
        "HS—11–12 (3B)": L3B["Networks and the Internet"],
      },
      "Data & Analysis": {
        "HS—9–10 (3A)": L3A["Data & Analysis"],
        "HS—11–12 (3B)": L3B["Data & Analysis"],
      },
      "Algorithms & Programming": {
        "HS—9–10 (3A)": L3A["Algorithms & Programming"],
        "HS—11–12 (3B)": L3B["Algorithms & Programming"],
      },
      "Impacts of Computing": {
        "HS—9–10 (3A)": L3A["Impacts of Computing"],
        "HS—11–12 (3B)": L3B["Impacts of Computing"],
      },
    },
  },
};

export default COMPUTER_SCIENCE_STANDARDS;
